import { useEffect, useMemo, useState } from "react";
import apiService, { formatRupees, handleError } from "../../Common/ApiCommon/ApiService";
import Table from "../../Common/Table";
import Pagination from "../../Common/Pagination";
import icons from "../../../assets/images/constantIcons";
import { useNavigate, useParams } from "react-router-dom";


const PlayerBookings = () => {
    const [PlayersData, setPlayersData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [viewOpen, setViewOpen] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();

    const handleView = (id) => {
        setViewOpen(id);
    };

    // Fetch customer data from the API
    useEffect(() => {
        const fetchPlayers = async () => {
            setLoading(true);
            try {
                const response = await apiService.get(`/customer_users/${id}/customer_booking_history`);
                setPlayersData(response.data);
            } catch (errors) {
                handleError(errors);
            } finally {
                setLoading(false);
            }
        };

        fetchPlayers();
    }, []);

    // Define table columns
    const columns = useMemo(
        () => [
            {
                Header: "id",
                accessor: "id",
                Cell: ({ row }) => {
                    return <div id={`booking-${row.original.id}`}>{row.original.id}</div>;
                },
            },
            {
                Header: "TABLE TYPE",
                accessor: "tableType",
                Cell: ({ value }) => {
                    return (
                        <div
                            className={`bg-green700 text-white rounded-3xl py-[6px] px-2 text-center text-wrap `}
                        >
                            {value}
                        </div>
                    );
                },
            },
            {
                Header: "PLAYERS",
                accessor: "players",
                headerAlign: "text-center",
                cellAlign: "text-center",
                Cell: ({ value }) => {
                    const playerNames = value.split(",").map((name) => name.trim());
                    return (
                        <div className="w-[240px] group ">
                            <ul className="flex flex-wrap justify-start lg:justify-center items-center">
                                {playerNames.map((name, index) => (
                                    <li key={index} className="break-words whitespace-normal">
                                        {name}
                                        {index !== playerNames.length - 1 && " / "}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                },
            },
            {
                Header: "Payable",
                accessor: "payable_players",
                Cell: ({ row }) => {
                    const payables = row.original.payable_players || [];
                    return (
                        <div className="w-[250px] group">
                            <div className="flex flex-wrap justify-start lg:justify-center items-center">
                                {payables.map(({ name, id }, index) => (
                                    <span
                                        key={id}
                                        className=""
                                    >
                                        {name}
                                        {index !== payables.length - 1 && " / "}

                                    </span>
                                ))}
                            </div>
                        </div>
                    );
                }
            },
            {
                Header: "Date",
                accessor: "date",
            },
            {
                Header: "Duration",
                accessor: "duration",
            },
            {
                Header: "Today AMT",
                accessor: "today_amount",
            },
            {
                Header: "Action",
                accessor: "action",
                disableSortBy: true,
                Cell: ({ row }) => {
                    return (
                        <div className="flex gap-3">
                            <div
                                onClick={() => handleView(row.original.id)}
                                className="flex items-center justify-center focus:outline-none w-[40px] h-[40px] rounded-full p-2 bg-green50 text-green cursor-pointer"
                            >
                                <icons.IoEyeOutline className="text-xl font-bold" />
                            </div>
                        </div>
                    )
                }
            }
        ],
        [viewOpen]
    );

    const data = useMemo(() => {
        if (!Array.isArray(PlayersData)) return [];

        return PlayersData.map((item) => {
            const paymentHistory = item.payment_history?.[0];
            const bookingTimes = paymentHistory?.Booking_times || {};

            return {
                id: item.id,
                tableType: item.table_name,
                players: item.players?.map((player) => player.name).join(", ") || "No players",
                payable_id: item.payable_id,
                payable_players: item.payable_players,
                Booking_times: {
                    start_time: bookingTimes.start_time || "",
                    end_time: bookingTimes.end_time || "",
                },
                duration: item.duration,
                today_amount: formatRupees(item.today_amount),
                date: item.date?.split("T")[0],
                table_charge: formatRupees(item.table_charge),
            };
        });
    }, [PlayersData]);


    // Paginate the data
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

    const selectedBooking = PlayersData?.find((booking) => booking.id === viewOpen);

    const playerDetails = selectedBooking ? [
        { label: "Booking Id", value: selectedBooking.id },
        { label: "Table", value: selectedBooking.table_name },
        { label: "Players", value: selectedBooking.players?.map((player) => player.name).join(", ") || "No players" },
        { label: "Payable", value: selectedBooking.payable_players.map((payable) => payable.name).join(", ") || "No payable" },
        { label: "Start Date", value: selectedBooking?.date?.split("T")[0] },
        { label: "Start Time", value: selectedBooking.start_time },
        { label: "Shift", value: selectedBooking.booking_type },
        { label: "Table Charge", value: `₹ ${selectedBooking.table_charge}` },
        { label: "End Date", value: selectedBooking?.end_date?.split("T")[0] || "N/A" },
        { label: "End Time", value: selectedBooking.end_time || "N/A" },
        { label: "Duration", value: selectedBooking.duration || "N/A" },
        { label: "Today Amount", value: formatRupees(selectedBooking.today_amount) },
        { label: "Previous Amount", value: formatRupees(selectedBooking.previous_amount) },
        { label: "Total Amount", value: formatRupees(selectedBooking.total_amount) },
        { label: "Discount", value: formatRupees(selectedBooking.discount) },
        { label: "Paid", value: formatRupees(selectedBooking.amount_to_be_pay) },
        {label: "Due",value: formatRupees( selectedBooking?.payment_history?.reduce((total, item) => {  return total + parseFloat(item.due || "0"); }, 0))},
        { label: "Payment Method",
            value: selectedBooking?.payment_history?.length > 0
                ? (() => {
                    const types = selectedBooking.payment_history
                      .map((item) => item.payment_type)
                      .filter((type) => type);
                    return types.length > 0 ? [...new Set(types)].join(", ") : "N/A";
                  })()
                : "N/A"
          }
    ] : [];

    return (
        <div >
            <div className=" flex flex-col ">

                <div className="flex flex-col">
                    <h1 className="main_heading text-lg sm:text-[22px] flex gap-3 items-center mb-3 mt-4">
                        <p>Player Booking Details</p>
                    </h1>
                </div>
                {loading ? (
                    <div className="flex justify-center items-center my-4">
                        <p className="text-green700 font-medium">Loading data...</p>
                    </div>
                ) : (
                    <div className="my-2 mb-6 overflow-auto vertical max-h-[70vh] horizontalscroll">
                        <Table columns={columns} data={currentRows} />
                    </div>
                )}

                {/* Pagination */}
                <Pagination
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={data.length}
                    onPageChange={setCurrentPage}
                    onRowsPerPageChange={(newRows) => {
                        setRowsPerPage(newRows);
                        setCurrentPage(1);
                    }}
                />
            </div>
            {/* view detail form */}
            {viewOpen && (
                <>
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                        <div className="bg-white rounded-lg p-6 w-[90%] max-w-lg  relative shadow-lg">
                            {/* Close Button */}
                            <div
                                className="absolute top-4 right-4 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px] flex justify-center items-center"
                                onClick={() => handleView(null)}
                            >
                                <icons.IoClose className="text-green700 text-xl" />
                            </div>
                            <h2 className="text-xl font-semibold mb-4 text-gray-700 text-start">
                                Booking Details
                            </h2>

                            <div className="overflow-x-auto h-[70vh] overflow-y-auto vertical">
                                <table className="category-details-table w-full border-collapse border border-gray-300">
                                    <thead className="">
                                        <tr className="bg-green700 text-white">
                                            <th className="border p-2  w-1/2">Field</th>
                                            <th className="border p-2  w-1/2">Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {playerDetails?.map((value, index) => (
                                            <tr key={index}>
                                                <td className="border p-2">{value.label}</td>
                                                <td className="border p-2">{value.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex justify-end space-x-4 pt-6">
                                <button
                                    type="button"
                                    onClick={() => handleView(null)}
                                    className="py-2 px-4 text-sm bg-white text-green rounded-lg border border-green"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PlayerBookings;
